import { validateExplicitManualLength } from './manualLength.js';
import { validateWallSplitLineage } from './wallSplitLineage.js';
import { uid } from './constants.js';
import { validateNativeMeasurement } from './nativeCapture.js';

const bounded = (value, min, max) => Number.isFinite(value) && value >= min && value <= max;
const validID = value => typeof value === 'string' && value.trim() === value && value.length > 0 && Array.from(value).length <= 80;
const point = value => value && bounded(value.x, -100000, 100000) && bounded(value.y, -100000, 100000);
const reference = value => value && validID(value.wallId) && ['a', 'b'].includes(value.end);
const timestamp = value => typeof value === 'string' && value.length <= 40 && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const floorID = plan => plan.activeFloorId || 'ground';

/** Live bindings preserve the original measured endpoint references. They do
 * not change the reading, timestamp, recorded coordinates or tolerance. */
export function currentMeasurementReferences(check) {
  const bindings=check?.endpointBindings;
  if(bindings!==undefined&&(!bindings||typeof bindings!=='object'||Array.isArray(bindings)||Object.keys(bindings).some(k=>!['a','b'].includes(k))))throw new Error('The measurement endpoint bindings are invalid.');
  const refs={};
  for(const slot of ['a','b']){
    const original=check?.[slot],bound=bindings?.[slot];
    if(bound!==undefined&&(!bound||Object.keys(bound).sort().join()!=='end,splitID,wallId'||!reference(bound)||bound.end!=='b'||!validID(bound.splitID)||!reference(original)||original.end!=='b'||bound.wallId===original.wallId))throw new Error('A measurement split binding has invalid original or current endpoints.');
    refs[slot]=bound?{wallId:bound.wallId,end:bound.end}:original;
  }
  return refs;
}
function endpoint(plan, ref, binding, original) {
  const walls = (plan.walls || []).filter(wall => wall.id === ref.wallId);
  if (!walls.length) return null;
  if (walls.length !== 1) throw new Error('A reference wall has a duplicated ID.');
  const wall = walls[0];
  if(binding){const lineage=validateWallSplitLineage(wall);if(lineage.splitID!==binding.splitID||lineage.part!=='b'||lineage.sourceWall.id!==original.wallId)throw new Error('The measurement endpoint no longer has its recorded split lineage.');}
  const result = ref.end === 'a' ? { x: wall.x1, y: wall.y1 } : { x: wall.x2, y: wall.y2 };
  if (!point(result)) throw new Error('A reference wall has invalid coordinates.');
  return result;
}

function validateEvidence(evidence, metres) {
  if (!evidence || typeof evidence !== 'object' || !['manual', 'lidar', 'bluetooth'].includes(evidence.source)
      || !timestamp(evidence.capturedAt) || !bounded(evidence.metres, 0.05, 10000)) throw new Error('Record a complete measurement with its source and timestamp.');
  if(evidence.manualInput&&Object.hasOwn(evidence.manualInput,'decimalSeparator')){
    if(evidence.source!=='manual')throw new Error('A device reading cannot also contain a manual decimal-format entry.');
    validateExplicitManualLength(evidence.manualInput,evidence.metres,{min:.05,max:10000});
  }
  if (evidence.source !== 'manual') validateNativeMeasurement(evidence, evidence.source === 'bluetooth' ? 'measureBluetooth' : 'measureLiDAR');
  if (Math.abs(evidence.metres - metres) > 1e-9) throw new Error('Use the original tool reading, or switch to manual entry for an adjusted measurement.');
  if (evidence.source === 'bluetooth' && !validID(evidence.readingID)) throw new Error('The tool reading has an invalid identity.');
  let serialized;
  try { serialized = JSON.stringify(evidence); } catch { throw new Error('The measurement evidence is invalid.'); }
  if (new TextEncoder().encode(serialized).length > 4096) throw new Error('The measurement evidence is too large.');
}

function validateCheck(check) {
  if (!check || !validID(check.id) || !validID(check.recordedFloorID) || typeof check.label !== 'string' || Array.from(check.label).length > 120
      || !reference(check.a) || !reference(check.b) || !point(check.recordedA) || !point(check.recordedB)
      || !bounded(check.measuredMetres, 0.05, 10000) || !bounded(check.toleranceMetres, 0.001, 1)
      || !bounded(check.recordedPlanMetres, 0.05, 300000) || !timestamp(check.capturedAt)) throw new Error('This measurement check has invalid or incomplete data.');
  if (check.a.wallId === check.b.wallId && check.a.end === check.b.end
      || Math.abs(distance(check.recordedA, check.recordedB) - check.recordedPlanMetres) > 1e-6) throw new Error('This measurement check has inconsistent reference points.');
  validateEvidence(check.sourceEvidence, check.measuredMetres);
  if (check.capturedAt !== check.sourceEvidence.capturedAt) throw new Error('This measurement check has an inconsistent timestamp.');
}

/** Floor-local corners, deduplicated by coincident coordinates. The first wall
 * endpoint owns a shared corner; deleting that wall invalidates its checks. */
export function measurementCorners(plan) {
  const found = new Map();
  for (const wall of plan.walls || []) {
    if (!validID(wall.id)) continue;
    for (const end of ['a', 'b']) {
      const p = end === 'a' ? { x: wall.x1, y: wall.y1 } : { x: wall.x2, y: wall.y2 };
      if (!point(p)) continue;
      const location = `${Math.round(p.x * 1e6)},${Math.round(p.y * 1e6)}`;
      if (!found.has(location)) found.set(location, { key: `${wall.id}:${end}`, wallId: wall.id, end, ...p });
    }
  }
  return [...found.values()];
}

/** Record a measured span without changing any wall, shape or measurement. A
 * mismatch is useful evidence and may be saved; it never becomes a constraint. */
export function createMeasurementCheck(plan, { a, b, measuredMetres, toleranceMetres = 0.05, label = '', evidence } = {}) {
  if (plan.measurementChecks !== undefined && !Array.isArray(plan.measurementChecks)) throw new Error('The saved measurement checks are invalid.');
  if ((plan.measurementChecks || []).length >= 200) throw new Error('This floor has reached its 200 measurement check limit.');
  if (!reference(a) || !reference(b)) throw new Error('Choose two existing wall corners on this floor.');
  const recordedA = endpoint(plan, a), recordedB = endpoint(plan, b);
  if (!recordedA || !recordedB) throw new Error('A selected wall no longer exists on this floor.');
  const capturedAt = evidence?.source === 'manual' || !evidence ? new Date().toISOString() : evidence.capturedAt;
  const sourceEvidence = !evidence || evidence.source === 'manual'
    ? { ...evidence, source: 'manual', metres: measuredMetres, capturedAt, reference: 'operator-entered' }
    : structuredClone(evidence);
  const check = { id: uid('measurement-'), label: typeof label === 'string' ? label.trim() : label,
    a: { wallId: a.wallId, end: a.end }, b: { wallId: b.wallId, end: b.end }, measuredMetres, toleranceMetres,
    capturedAt, sourceEvidence, recordedFloorID: floorID(plan), recordedA, recordedB, recordedPlanMetres: distance(recordedA, recordedB) };
  validateCheck(check);
  const doc = structuredClone(plan); doc.measurementChecks = [...(doc.measurementChecks || []), check];
  return { doc, check };
}

/** Recompute instead of storing a success flag. A changed endpoint or floor
 * always requires a fresh check, even if the new span matches the old reading. */
export function evaluateMeasurementCheck(plan, check) {
  try {
    validateCheck(check);
    const refs=currentMeasurementReferences(check);
    const a = endpoint(plan, refs.a,check.endpointBindings?.a,check.a), b = endpoint(plan, refs.b,check.endpointBindings?.b,check.b);
    if (!a || !b) return { status: 'missing-reference', planMetres: null, errorMetres: null, message: 'A reference wall is no longer on this floor.' };
    const planMetres = distance(a, b), signedDifferenceMetres = planMetres - check.measuredMetres;
    const result = { planMetres, errorMetres: Math.abs(signedDifferenceMetres), signedDifferenceMetres, a, b };
    if (floorID(plan) !== check.recordedFloorID || distance(a, check.recordedA) > 1e-6 || distance(b, check.recordedB) > 1e-6) {
      return { ...result, status: 'geometry-changed', message: 'The floor or reference corners changed. Record a new measurement check.' };
    }
    // A nanometre allowance prevents binary rounding from failing an exact
    // tolerance boundary (for example, 1.05 m measured against a 1 m span).
    return { ...result, status: result.errorMetres <= check.toleranceMetres + 1e-9 ? 'within-tolerance' : 'outside-tolerance' };
  } catch (error) { return { status: 'invalid', planMetres: null, errorMetres: null, message: error.message }; }
}
